import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useMemo, useState } from 'react';
import { Button, RadioGroup, RadioInput } from '@airia/ui';
import { useAddinChatActions } from '../AddinChatActionsContext';
import { MessageRadiosSchema, parseDropdownOptions } from './schemas';
/**
 * Renders `<airiaMessageRadios>` as a single-select group with a Send button.
 * Tag body is a JSON array of `{ label, message }` parsed at render time.
 * Selection persists until Send is clicked; the chosen option's `message`
 * field is submitted as the user's next message.
 */
export function MessageRadios(props) {
    const { children, artifactContent, buttonText, sendButtonText, isLoading } = MessageRadiosSchema.parse(props);
    const actions = useAddinChatActions();
    const headingId = useId();
    const optionsSource = artifactContent || children;
    const options = useMemo(() => parseDropdownOptions(optionsSource), [optionsSource]);
    const [selected, setSelected] = useState(null);
    // Reset selection if the options list length changes mid-stream so a
    // stale index can never reference a now-different message.
    useEffect(() => {
        setSelected(null);
    }, [options.length]);
    if (options.length === 0)
        return null;
    const disabled = !actions || actions.isExecuting || isLoading;
    const sendLabel = sendButtonText.length > 0 ? sendButtonText : 'Send';
    const heading = buttonText.length > 0 ? buttonText : 'Choose one';
    const handleSend = () => {
        if (!actions || selected === null)
            return;
        const option = options[parseInt(selected, 10)];
        if (!option)
            return;
        void actions.submitMessage(option.message);
        setSelected(null);
    };
    return (_jsxs("div", { className: "my-2 flex flex-col gap-3", children: [_jsx("div", { id: headingId, className: "text-sm font-medium", children: heading }), _jsx(RadioGroup, { "aria-labelledby": headingId, value: selected ?? '', onValueChange: setSelected, disabled: disabled, className: "gap-2", children: options.map((option, index) => (_jsx(RadioInput, { value: String(index), label: option.label, disabled: disabled }, index))) }), _jsx(Button, { size: "sm", variant: "outline", className: "self-start", disabled: disabled || selected === null, onClick: handleSend, children: sendLabel })] }));
}
